import React from 'react';
import { Card, Grid, IconButton, useTheme } from '@mui/material';
import { Dealer } from 'interfaces/dealerLocator';
import { useStyles } from './DealerInfoCard.styles';
import { Icon, Icons } from 'components/Global/Icon/Icon';
import { appPalette } from 'theme/App.palette';
import { useDispatch, useSelector } from 'react-redux';
import {
  preferredDealer,
  setPreferredDealerId,
  removePreferredDealerId,
} from 'redux/slices/dealerSlice';
import TextRender from 'components/Global/TextRender/TextRender';
import { trackEvent } from 'helpers/Analytics';
import {
  LinkButton,
  LinkButtonType,
} from 'components/Global/LinkButton/LinkButton';

export interface IDealerInfoCard {
  dealer: Dealer;
}

export function DealerInfoCard({ dealer }: IDealerInfoCard): JSX.Element {
  const classes = useStyles();
  const theme = useTheme();
  const isHondaTheme = theme.themeName === 'honda';
  const { light_grey_2, iconMainColor } = appPalette.colors;
  const currentPreferredDealer = useSelector(preferredDealer);
  const dispatch = useDispatch(); // redux dispatch

  const handlePreferredDealerId = () => {
    let actionLabel = 'results:deselect preferred dealer';
    if (dealer.dealerNumber === currentPreferredDealer) {
      dispatch(removePreferredDealerId());
    } else {
      actionLabel = 'results:select preferred dealer';
      dispatch(setPreferredDealerId(dealer.dealerNumber as string));
    }

    trackEvent({
      label: actionLabel,
      category: {
        includePageName: true,
        text: 'results',
      },
      type: 'selector',
      dealer: {
        dealerId: dealer.dealerNumber,
        dealerZipcode: dealer.zipCode,
        dealerName: dealer.name,
        dealerAddress: `${dealer.address as string}, ${dealer.city}, ${
          dealer.state
        } ${dealer.zipCode}`,
      },
    });
  };

  const handleDealerDirectionClick = () => {
    trackEvent({
      label: `results:${dealer.name} directions`,
      category: {
        includePageName: true,
        text: 'results',
      },
      type: 'link',
      dealer: {
        dealerId: dealer.dealerNumber,
        dealerZipcode: dealer.zipCode,
        dealerName: dealer.name,
        dealerAddress: `${dealer.address as string}, ${dealer.city}, ${
          dealer.state
        } ${dealer.zipCode}`,
      },
    });
  };

  const handleDealerPhoneClick = () => {
    trackEvent({
      label: `results:call ${dealer.name}`,
      category: {
        includePageName: true,
        text: 'results',
      },
      type: 'link',
      dealer: {
        dealerId: dealer.dealerNumber,
        dealerZipcode: dealer.zipCode,
        dealerName: dealer.name,
        dealerAddress: `${dealer.address as string}, ${dealer.city}, ${
          dealer.state
        } ${dealer.zipCode}`,
        dealerPhone: dealer.phone,
      },
    });
  };

  const getDealerBingDirection = dealer
    ? `http://bing.com/maps/default.aspx?rtp=~adr.${dealer.address?.replace(
        ' ',
        '%20'
      )}%20${dealer.city?.replace(' ', '%20')},%20${dealer.state?.replace(
        ' ',
        '%20'
      )}%20${dealer.zipCode?.replace(' ', '%20')}`
    : '';

  return (
    <Card className={`${classes.root} dealer-info-card`}>
      <Grid container direction="row" className={classes.dealerHeader}>
        <h3
          className={`${classes.dealerTitle} ${
            isHondaTheme ? 'atm-secondary-title-5' : 'acr-caption-5'
          }`}
        >
          <TextRender text={dealer.name || ''} />
        </h3>
      </Grid>
      <Grid component="div">
        <IconButton
          disableRipple
          onClick={() => handlePreferredDealerId()}
          className={`${classes.preferredButton} ${
            currentPreferredDealer === dealer.dealerNumber ? 'is-preferred' : ''
          } preferred-button`}
          aria-label={`Select ${dealer.name} as preferred dealer`}
          size="large"
        >
          <Icon
            color={light_grey_2}
            type={
              currentPreferredDealer === dealer.dealerNumber
                ? Icons.preferredDealerSelected
                : Icons.preferredDealer
            }
          />
        </IconButton>
        <Grid className={classes.directionContainer} container direction="row">
          <Grid item xs={10} md={9} lg={7}>
            <LinkButton
              variant={LinkButtonType.simple}
              target="_blank"
              to={getDealerBingDirection}
              isExternal={true}
              onClick={() => {
                handleDealerDirectionClick();
              }}
              classNames={{ container: classes.directionLink }}
              ariaLabel={`Go to the ${dealer.name} address`}
            >
              <Grid container direction="row">
                <Grid item xs={1} className={classes.directionIcon}>
                  <Icon
                    type={
                      isHondaTheme ? Icons.filledDirections : Icons.directions
                    }
                    color={iconMainColor}
                  />
                </Grid>
                <Grid
                  item
                  xs={10}
                  className={`${isHondaTheme ? 'atm-paragraph-5' : ''}`}
                >
                  <TextRender tagName="p" text={dealer.address as string} />{' '}
                  <TextRender
                    tagName="p"
                    text={`${dealer.city}, ${dealer.state} ${dealer.zipCode}`}
                  />
                </Grid>
              </Grid>
            </LinkButton>
          </Grid>
          <Grid item xs={2} md={3} lg={5} className={classes.telephone}>
            <LinkButton
              classNames={{ container: classes.telephoneLink }}
              to={`tel:${dealer.phone}`}
              isExternal={true}
              onClick={() => {
                handleDealerPhoneClick();
              }}
            >
              <Icon
                type={isHondaTheme ? Icons.filledTelephone : Icons.telephone}
                color={iconMainColor}
              />
              <p
                className={`${
                  isHondaTheme ? 'atm-paragraph-5' : 'acr-paragraph-5'
                } ${classes.telephoneText}`}
              >
                <TextRender text={`${dealer.phone}`} />
              </p>
            </LinkButton>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  );
}
